// Licence / activation system (Supabase-backed) — "Recommended tier":
//   • Licence keys live in Supabase and are created by YOU (the vendor).
//   • Each device activates against a key and counts toward the key's device
//     limit, so one key can't be shared across unlimited terminals.
//   • The app re-validates online once a day but keeps working OFFLINE for a
//     grace period, so a till never dies just because the internet dropped.
//   • A fresh install gets a local trial so it's usable before a key is bought.
//
// Talks to Supabase over PostgREST with `fetch` (see services/supabase.ts).
//
// ── One-time setup (Supabase dashboard → SQL editor) ──────────────────────────
//
//   create table if not exists public.licenses (
//     key           text primary key,        -- e.g. EPOS-XXXX-XXXX-XXXX
//     plan          text not null default 'monthly',
//     status        text not null default 'active',  -- active | suspended | revoked
//     expires_at    timestamptz,             -- null = never expires (lifetime)
//     max_devices   int  not null default 1,
//     customer_name text,
//     created_at    timestamptz not null default now()
//   );
//
//   create table if not exists public.activations (
//     id          uuid primary key default gen_random_uuid(),
//     license_key text not null references public.licenses(key) on delete cascade,
//     device_id   text not null,
//     device_name text,
//     last_seen   timestamptz not null default now(),
//     created_at  timestamptz not null default now(),
//     unique (license_key, device_id)
//   );
//
//   alter table public.licenses    enable row level security;
//   alter table public.activations enable row level security;
//
//   -- Clients may READ licences (to validate a key) but NOT create/edit them;
//   -- you mint licences from the dashboard or with the service-role key.
//   create policy "licenses read" on public.licenses
//     for select to anon using (true);
//
//   -- Clients fully manage their own device activations.
//   create policy "activations all" on public.activations
//     for all to anon using (true) with check (true);
//
// Security note: with the publishable (anon) key the device-limit is enforced
// client-side, so a technical user could bypass it. That's the accepted
// trade-off for this tier; the "Hardened" tier moves activation into a signed
// Edge Function to close that gap.

import { REST, sbHeaders, sbError } from './supabase';
import { cachedKey, cachedRawKey } from './idb';

// TEMPORARY: set true to bypass all licence checks (validate() always returns
// an active status, no network call, no activation screen). Flip back to false
// to re-enable the licence gate.
const LICENSE_CHECK_DISABLED = true;

// How many days the app keeps running offline after the last successful online
// validation before it demands a reconnect.
const GRACE_DAYS = 14;
// Length of the automatic local trial on a fresh install. Set to 0 to require a
// licence key immediately (no trial).
const TRIAL_DAYS = 15;
// Re-validate online at most this often (ms). Between checks the cached state is
// trusted, so launches stay fast and offline-friendly.
const RECHECK_MS = 24 * 60 * 60 * 1000;

// Local storage keys — deliberately OUTSIDE the `sconnect.` namespace so the
// cloud backup/restore feature never carries one device's activation onto
// another machine.
const deviceIdCache = cachedRawKey('epos.deviceId', '');
const deviceNameCache = cachedRawKey('epos.deviceName', '');
const trialStartCache = cachedRawKey('epos.trialStart', '');

export type LicensePhase =
  | 'trial'
  | 'active'
  | 'grace' // valid but running on cached state (offline)
  | 'expired'
  | 'revoked'
  | 'unactivated';

export interface LicenseStatus {
  phase: LicensePhase;
  ok: boolean; // whether the app is allowed to run
  plan?: string;
  key?: string;
  expiresAt?: string | null;
  daysLeft?: number | null; // until expiry; null = never expires
  offline?: boolean;
  message: string;
}

interface LicenseRow {
  key: string;
  plan: string;
  status: string;
  expires_at: string | null;
  max_devices: number;
  customer_name: string | null;
}

interface ActivationRow {
  device_id: string;
}

// Locally cached licence state, used to run offline within the grace window.
interface LicenseCache {
  key: string;
  plan: string;
  status: string;
  expiresAt: string | null;
  lastValidated: string; // ISO
  graceUntil: string; // ISO — lastValidated + GRACE_DAYS
}

// ── small helpers ─────────────────────────────────────────────────────────────

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getDeviceId(): string {
  let id = deviceIdCache.get();
  if (!id) {
    id = uuid();
    deviceIdCache.set(id);
  }
  return id;
}

export function getDeviceName(): string {
  return deviceNameCache.get();
}

export function setDeviceName(name: string): void {
  deviceNameCache.set(name.trim());
}

// Cached licence state (JSON), for offline grace-window validation.
const licenseCache = cachedKey<LicenseCache | null>('epos.license', null);

function readCache(): LicenseCache | null {
  return licenseCache.get();
}

function writeCache(c: LicenseCache | null): void {
  if (c) licenseCache.set(c);
  else licenseCache.clear();
}

function daysBetween(from: Date, to: Date): number {
  return Math.ceil((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000));
}

function isExpired(expiresAt: string | null): boolean {
  return expiresAt != null && new Date(expiresAt).getTime() < Date.now();
}

function buildCache(row: LicenseRow): LicenseCache {
  const now = new Date();
  return {
    key: row.key,
    plan: row.plan,
    status: row.status,
    expiresAt: row.expires_at,
    lastValidated: now.toISOString(),
    graceUntil: new Date(now.getTime() + GRACE_DAYS * 24 * 60 * 60 * 1000).toISOString(),
  };
}

function activeStatus(c: LicenseCache, offline: boolean): LicenseStatus {
  return {
    phase: offline ? 'grace' : 'active',
    ok: true,
    plan: c.plan,
    key: c.key,
    expiresAt: c.expiresAt,
    daysLeft: c.expiresAt ? daysBetween(new Date(), new Date(c.expiresAt)) : null,
    offline,
    message: offline
      ? `Running offline — will need to reconnect by ${new Date(c.graceUntil).toLocaleDateString()}.`
      : 'Licence active.',
  };
}

// ── trial ─────────────────────────────────────────────────────────────────────

function trialStatus(): LicenseStatus {
  if (TRIAL_DAYS <= 0) {
    return { phase: 'unactivated', ok: false, message: 'Enter a licence key to activate.' };
  }
  let start = trialStartCache.get();
  if (!start) {
    start = new Date().toISOString();
    trialStartCache.set(start);
  }
  const ends = new Date(new Date(start).getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
  const left = daysBetween(new Date(), ends);
  if (left > 0) {
    return {
      phase: 'trial',
      ok: true,
      daysLeft: left,
      expiresAt: ends.toISOString(),
      message: `Trial — ${left} day${left === 1 ? '' : 's'} left. Enter a licence key to activate.`,
    };
  }
  return {
    phase: 'expired',
    ok: false,
    message: 'Your trial has ended. Enter a licence key to continue.',
  };
}

// ── Supabase calls ────────────────────────────────────────────────────────────

async function fetchLicense(key: string): Promise<LicenseRow | null> {
  const url =
    `${REST}/licenses` +
    `?select=key,plan,status,expires_at,max_devices,customer_name` +
    `&key=eq.${encodeURIComponent(key)}&limit=1`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) {
    if (res.status === 404) {
      throw new Error('Licence tables not set up in Supabase yet (see setup SQL in license.ts).');
    }
    throw await sbError(res);
  }
  const rows = (await res.json()) as LicenseRow[];
  return rows[0] ?? null;
}

async function listActivations(key: string): Promise<ActivationRow[]> {
  const url =
    `${REST}/activations?select=device_id&license_key=eq.${encodeURIComponent(key)}`;
  const res = await fetch(url, { headers: sbHeaders() });
  if (!res.ok) throw await sbError(res);
  return (await res.json()) as ActivationRow[];
}

// Insert or refresh this device's activation row (upsert on the
// (license_key, device_id) unique constraint).
async function upsertActivation(key: string): Promise<void> {
  const res = await fetch(`${REST}/activations`, {
    method: 'POST',
    headers: sbHeaders({
      Prefer: 'resolution=merge-duplicates,return=minimal',
    }),
    body: JSON.stringify({
      license_key: key,
      device_id: getDeviceId(),
      device_name: getDeviceName() || null,
      last_seen: new Date().toISOString(),
    }),
  });
  if (!res.ok) throw await sbError(res);
}

// Touch last_seen for an already-activated device. Returns false if the
// activation row no longer exists server-side (admin kicked this device).
async function touchActivation(key: string): Promise<boolean> {
  const url =
    `${REST}/activations` +
    `?license_key=eq.${encodeURIComponent(key)}&device_id=eq.${encodeURIComponent(getDeviceId())}`;
  const res = await fetch(url, {
    method: 'PATCH',
    headers: sbHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify({ last_seen: new Date().toISOString() }),
  });
  if (!res.ok) throw await sbError(res);
  const rows = (await res.json()) as unknown[];
  return rows.length > 0;
}

// ── public API ────────────────────────────────────────────────────────────────

// Turn a licence row into a runtime status, or throw a user-facing error if the
// licence is not usable (suspended/revoked/expired/device-limit).
function assertUsable(row: LicenseRow): void {
  if (row.status === 'revoked') throw new Error('This licence has been revoked.');
  if (row.status === 'suspended') throw new Error('This licence is suspended. Contact support.');
  if (isExpired(row.expires_at)) throw new Error('This licence has expired.');
}

// Activate a freshly entered key on this device.
export async function activate(key: string): Promise<LicenseStatus> {
  const k = key.trim().toUpperCase();
  if (!k) throw new Error('Enter a licence key.');

  const row = await fetchLicense(k);
  if (!row) throw new Error('Licence key not found.');
  assertUsable(row);

  const acts = await listActivations(k);
  const already = acts.some((a) => a.device_id === getDeviceId());
  if (!already && acts.length >= row.max_devices) {
    throw new Error(
      `Device limit reached (${row.max_devices}). Deactivate another device first.`
    );
  }

  await upsertActivation(k);
  const cache = buildCache(row);
  writeCache(cache);
  return activeStatus(cache, false);
}

// Validate the current device's licence, called on launch. Re-checks online at
// most once per RECHECK_MS; otherwise trusts the cache. Falls back to the
// offline grace window when the network is unavailable.
export async function validate(): Promise<LicenseStatus> {
  if (LICENSE_CHECK_DISABLED) {
    return {
      phase: 'active',
      ok: true,
      daysLeft: null,
      message: 'Licence check temporarily disabled.',
    };
  }

  const cache = readCache();

  // No key on this device → trial / unactivated.
  if (!cache) return trialStatus();

  // Recently validated → trust the cache without a network round-trip, unless
  // it has since expired.
  const sinceCheck = Date.now() - new Date(cache.lastValidated).getTime();
  if (sinceCheck < RECHECK_MS && !isExpired(cache.expiresAt)) {
    return activeStatus(cache, false);
  }

  // Time to re-check online.
  try {
    const row = await fetchLicense(cache.key);
    if (!row) {
      writeCache(null);
      return { phase: 'revoked', ok: false, message: 'Licence no longer exists. Contact support.' };
    }
    if (row.status === 'revoked' || row.status === 'suspended') {
      writeCache(null);
      return {
        phase: 'revoked',
        ok: false,
        key: row.key,
        message:
          row.status === 'revoked'
            ? 'This licence has been revoked.'
            : 'This licence is suspended. Contact support.',
      };
    }
    if (isExpired(row.expires_at)) {
      const fresh = buildCache(row);
      writeCache(fresh);
      return {
        phase: 'expired',
        ok: false,
        key: row.key,
        plan: row.plan,
        expiresAt: row.expires_at,
        message: 'This licence has expired. Renew to continue.',
      };
    }
    // Still valid — make sure this device is still registered. If the admin
    // removed it, lock out.
    const stillActivated = await touchActivation(cache.key);
    if (!stillActivated) {
      writeCache(null);
      return {
        phase: 'revoked',
        ok: false,
        message: 'This device was deactivated. Re-enter the licence key to reactivate.',
      };
    }
    const fresh = buildCache(row);
    writeCache(fresh);
    return activeStatus(fresh, false);
  } catch (err) {
    // Network/offline: fall back to the grace window.
    const now = Date.now();
    if (isExpired(cache.expiresAt)) {
      return { phase: 'expired', ok: false, key: cache.key, message: 'Licence expired.' };
    }
    if (now <= new Date(cache.graceUntil).getTime()) {
      return activeStatus(cache, true);
    }
    return {
      phase: 'expired',
      ok: false,
      key: cache.key,
      message:
        'Could not verify your licence online and the offline grace period has ended. ' +
        'Connect to the internet to continue.',
    };
  }
}

// Synchronous, cache-only status for UI that can't await (no network call).
export function cachedStatus(): LicenseStatus {
  const cache = readCache();
  if (!cache) return trialStatus();
  if (isExpired(cache.expiresAt)) {
    return { phase: 'expired', ok: false, key: cache.key, message: 'Licence expired.' };
  }
  const offline = Date.now() - new Date(cache.lastValidated).getTime() >= RECHECK_MS;
  return activeStatus(cache, offline);
}

// Release this device's activation (frees a slot) and clear local licence state.
export async function deactivate(): Promise<void> {
  const cache = readCache();
  if (cache) {
    const url =
      `${REST}/activations` +
      `?license_key=eq.${encodeURIComponent(cache.key)}` +
      `&device_id=eq.${encodeURIComponent(getDeviceId())}`;
    try {
      await fetch(url, { method: 'DELETE', headers: sbHeaders() });
    } catch {
      /* best-effort; clear locally regardless */
    }
  }
  writeCache(null);
}
